"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.candidateBookSchema = exports.INTERVIEW_LINK_HEADER = exports.interviewListQuerySchema = exports.INTERVIEW_LIST_VIEWS = exports.updateRoomSchema = exports.roomInputSchema = exports.ROOM_NAME_MAX = exports.setInterviewPanelSchema = exports.PLANNER_PANEL_MAX = exports.setInterviewTimeSchema = exports.interviewDetailsSchema = exports.INTERVIEW_NOTES_MAX = exports.recordOutcomeSchema = exports.cancelInterviewSchema = exports.substitutePanelistSchema = exports.cantMakeItSchema = exports.rescheduleInterviewSchema = exports.bookInterviewSchema = exports.interviewLengthSchema = exports.INTERVIEW_LENGTH_CHOICES = exports.offerSlotsSchema = exports.MEETING_LINK_MAX = exports.HOUR_REMINDER_MINUTES = exports.DAY_REMINDER_HOURS = exports.SLOT_REMINDER_HOURS = exports.ALL_FREE_SLOTS_MAX = exports.OFFER_SLOTS_MAX = exports.SCHEDULER_HORIZON_DAYS = exports.PANELIST_STATE_LABELS = exports.PANELIST_STATES = exports.RESCHEDULE_INITIATOR_LABELS = exports.RESCHEDULE_INITIATORS = exports.CANCEL_CODE_NEEDING_NOTE = exports.INTERVIEW_CANCEL_CODES = exports.INTERVIEW_CANCEL_REASONS = exports.INTERVIEW_OUTCOMES = exports.UNBOOKED_INTERVIEW_STATUSES = exports.BOOKED_INTERVIEW_STATUSES = exports.OPEN_INTERVIEW_STATUSES = exports.INTERVIEW_STATUS_LABELS = void 0;
exports.cancelReasonLabel = cancelReasonLabel;
exports.slotReminderDue = slotReminderDue;
exports.isDayReminderDue = isDayReminderDue;
exports.isHourReminderDue = isHourReminderDue;
exports.isInsideReminderWindow = isInsideReminderWindow;
exports.interviewEndsAt = interviewEndsAt;
exports.mayRecordOutcome = mayRecordOutcome;
exports.interviewPlace = interviewPlace;
exports.interviewMessageText = interviewMessageText;
const zod_1 = require("zod");
const enums_1 = require("./enums");
const schemas_1 = require("./schemas");
// S19 — interview scheduling (SCH-01…05). Sources: master plan S19 and its planning
// decisions (DECISIONS-LOG S19 Q1–Q9); F10.01–F10.14, F2.12; E8; D5, D6, D16; §L5; §L7;
// §L19 interview cancellation; §L22 N-27–N-36 and N-77; §L23; §L25 interview.
exports.INTERVIEW_STATUS_LABELS = {
    unscheduled: 'To schedule',
    slots_offered: 'Times sent',
    scheduled: 'Booked',
    confirmed: 'Confirmed',
    rescheduled: 'Being moved',
    completed: 'Completed',
    candidate_no_show: 'Candidate no-show',
    panelist_no_show: 'Panelist no-show',
    cancelled: 'Cancelled',
};
/** Still to happen — the rows a calendar, a reminder or a "to schedule" list cares about. */
exports.OPEN_INTERVIEW_STATUSES = ['unscheduled', 'slots_offered', 'scheduled', 'confirmed', 'rescheduled'];
/** A time is fixed: panel and room are taken, reminders are due. */
exports.BOOKED_INTERVIEW_STATUSES = ['scheduled', 'confirmed'];
/** Waiting for someone to fix a time — the SCH-02 "To schedule" list, and N-36. */
exports.UNBOOKED_INTERVIEW_STATUSES = ['unscheduled', 'slots_offered', 'rescheduled'];
/** F10.10–F10.12: the three outcomes (master plan S19). */
exports.INTERVIEW_OUTCOMES = ['completed', 'candidate_no_show', 'panelist_no_show'];
/** §L19 "Interview cancellation" (S19 Q6). Never a rejection. */
exports.INTERVIEW_CANCEL_REASONS = [
    { code: 'ICX-PANEL', label: 'Panel unavailable' },
    { code: 'ICX-WITHDREW', label: 'Candidate withdrew' },
    { code: 'ICX-POSITION', label: 'Position on hold or closed' },
    { code: 'ICX-MISTAKE', label: 'Scheduled by mistake' },
    { code: 'ICX-OTHER', label: 'Other' },
];
exports.INTERVIEW_CANCEL_CODES = exports.INTERVIEW_CANCEL_REASONS.map((reason) => reason.code);
/** §L19: "Other" needs a note. */
exports.CANCEL_CODE_NEEDING_NOTE = 'ICX-OTHER';
function cancelReasonLabel(code) {
    if (!code)
        return null;
    return exports.INTERVIEW_CANCEL_REASONS.find((reason) => reason.code === code)?.label ?? code;
}
/** F10.07 / §L25: who asked to move it. The candidate never moves it themselves (S19 Q4). */
exports.RESCHEDULE_INITIATORS = ['candidate', 'panelist', 'recruiter'];
exports.RESCHEDULE_INITIATOR_LABELS = {
    candidate: 'The candidate asked',
    panelist: 'A panelist asked',
    recruiter: 'We moved it',
};
/** §L5 step 3: how each panelist stands at a slot. */
exports.PANELIST_STATES = ['available', 'outside_hours', 'blocked_out', 'booked'];
exports.PANELIST_STATE_LABELS = {
    available: 'Free',
    outside_hours: 'Outside their hours',
    blocked_out: 'Away',
    booked: 'Another interview',
};
// --- Timing ------------------------------------------------------------------------------
/** How far ahead the scheduler looks, and what "every free time" covers (S19 Q3: two weeks). */
exports.SCHEDULER_HORIZON_DAYS = 14;
/** §L5 step 5: "3–5 slots typical" — a ceiling on the hand-picked list. */
exports.OFFER_SLOTS_MAX = 10;
/** Every free time for two weeks, capped so the email stays readable. */
exports.ALL_FREE_SLOTS_MAX = 60;
/** §L22 N-28: +24h and +48h after the times went. */
exports.SLOT_REMINDER_HOURS = [24, 48];
/** §L22 N-30 and F10.06: the day-before reminder, and auto-confirm at the same moment. */
exports.DAY_REMINDER_HOURS = 24;
/** §L22 N-31. */
exports.HOUR_REMINDER_MINUTES = 60;
const HOUR_MS = 3_600_000;
const MINUTE_MS = 60_000;
/** Which N-28 reminder (0-based) is due now, or null. Counted from when the times went. */
function slotReminderDue(input) {
    if (!input.offeredAt || input.sent >= exports.SLOT_REMINDER_HOURS.length)
        return null;
    const hours = exports.SLOT_REMINDER_HOURS[input.sent] ?? Number.POSITIVE_INFINITY;
    return input.now.getTime() >= input.offeredAt.getTime() + hours * HOUR_MS ? input.sent : null;
}
/** N-30 and the T-24h auto-confirm fall due together, until the interview begins. */
function isDayReminderDue(input) {
    if (!input.startsAt || input.sentAt)
        return false;
    const now = input.now.getTime();
    return now >= input.startsAt.getTime() - exports.DAY_REMINDER_HOURS * HOUR_MS && now < input.startsAt.getTime();
}
function isHourReminderDue(input) {
    if (!input.startsAt || input.sentAt)
        return false;
    const now = input.now.getTime();
    return now >= input.startsAt.getTime() - exports.HOUR_REMINDER_MINUTES * MINUTE_MS && now < input.startsAt.getTime();
}
/**
 * An interview booked inside the last day: its booking email already says when, so N-30
 * is not sent separately and the booking itself stands as confirmed at T-24h — the same
 * shape as S18's short-notice exam (S18 Q9).
 */
function isInsideReminderWindow(startsAt, bookedAt) {
    return startsAt.getTime() - bookedAt.getTime() < exports.DAY_REMINDER_HOURS * HOUR_MS;
}
function interviewEndsAt(startsAt, durationMinutes) {
    return new Date(startsAt.getTime() + Math.max(durationMinutes, 1) * MINUTE_MS);
}
/** F10.10: completed is recorded once the scheduled time has passed. */
function mayRecordOutcome(startsAt, now) {
    return startsAt !== null && now.getTime() >= startsAt.getTime();
}
// --- Inputs ------------------------------------------------------------------------------
const isoSchema = zod_1.z.iso.datetime({ message: 'Pick a date and time.' });
const NOTE_MAX = 1000;
exports.MEETING_LINK_MAX = 500;
/**
 * F10.03 — send times. Hand-picked (1 to 10), or, when the round allows self-booking,
 * none: the server sends every full-panel free time for two weeks (S19 Q3).
 */
exports.offerSlotsSchema = schemas_1.versionedSchema.extend({
    startsAt: zod_1.z.array(isoSchema).max(exports.OFFER_SLOTS_MAX, `Offer at most ${exports.OFFER_SLOTS_MAX} times.`),
});
const meetingLinkSchema = zod_1.z
    .url('Paste the full meeting link, starting with https://')
    .max(exports.MEETING_LINK_MAX)
    .nullable();
/**
 * F10.05 — book directly. A clash with a panelist's other interview is a warning the
 * recruiter accepts (acceptance criterion "panelist double-booking warned").
 */
/** C5-c (DECISIONS-LOG 2026-11-01 Q4): a booking may give this one interview its own length; the rest stay an hour. */
exports.INTERVIEW_LENGTH_CHOICES = [30, 45, 60, 90, 120];
exports.interviewLengthSchema = zod_1.z.number().int('Whole minutes only.').min(15, 'At least 15 minutes.').max(480, 'At most 8 hours.');
exports.bookInterviewSchema = schemas_1.versionedSchema.extend({
    startsAt: isoSchema,
    meetingLink: meetingLinkSchema.optional(),
    roomId: schemas_1.uuidSchema.nullable().optional(),
    isConflictAccepted: zod_1.z.boolean().default(false),
    /** Absent keeps the length this interview already has (an hour, unless changed before). */
    durationMinutes: exports.interviewLengthSchema.optional(),
});
/**
 * F10.07 — reschedule. Records who asked; with a new time it is booked in the same step,
 * without one it waits as `rescheduled` for times to be sent or a time booked.
 */
exports.rescheduleInterviewSchema = schemas_1.versionedSchema.extend({
    initiator: zod_1.z.enum(exports.RESCHEDULE_INITIATORS),
    /** The panelist who asked, when a panelist did. */
    panelistUserId: schemas_1.uuidSchema.nullable().default(null),
    note: zod_1.z.string().trim().max(NOTE_MAX).nullable().default(null),
    startsAt: isoSchema.nullable().default(null),
    isConflictAccepted: zod_1.z.boolean().default(false),
});
/** F10.07 by a panelist: "I can't make it" on their own interview. */
exports.cantMakeItSchema = schemas_1.versionedSchema.extend({
    note: zod_1.z.string().trim().max(NOTE_MAX).nullable().default(null),
});
/** F10.08 — this interview only; the plan never changes (§L25). */
exports.substitutePanelistSchema = schemas_1.versionedSchema.extend({
    fromUserId: schemas_1.uuidSchema,
    toUserId: schemas_1.uuidSchema,
    isConflictAccepted: zod_1.z.boolean().default(false),
});
/** F10.09 — a reason, always; "Other" needs a note (§L19, S19 Q6). */
exports.cancelInterviewSchema = schemas_1.versionedSchema
    .extend({
    reasonCode: zod_1.z.enum(exports.INTERVIEW_CANCEL_CODES, { message: 'Pick a reason before continuing.' }),
    note: zod_1.z.string().trim().max(NOTE_MAX).nullable().default(null),
})
    .refine((value) => value.reasonCode !== exports.CANCEL_CODE_NEEDING_NOTE || (value.note ?? '') !== '', {
    message: 'Say why it is cancelled.',
    path: ['note'],
});
/** F10.10–F10.12. A panelist no-show names who did not come (RPT-04 counts them). */
exports.recordOutcomeSchema = schemas_1.versionedSchema
    .extend({
    outcome: zod_1.z.enum(exports.INTERVIEW_OUTCOMES),
    absentUserIds: zod_1.z.array(schemas_1.uuidSchema).max(20).default([]),
})
    .refine((value) => value.outcome !== 'panelist_no_show' || value.absentUserIds.length > 0, {
    message: 'Choose who did not come.',
    path: ['absentUserIds'],
});
/** F10.13 and F10.14 — the link for online, the room for in person. */
exports.INTERVIEW_NOTES_MAX = 500;
exports.interviewDetailsSchema = schemas_1.versionedSchema.extend({
    meetingLink: meetingLinkSchema.optional(),
    roomId: schemas_1.uuidSchema.nullable().optional(),
    /** R7 PLN-01 "Where": this interview only; the round's plan never changes (§L25). */
    mode: zod_1.z.enum(enums_1.INTERVIEW_MODES).optional(),
    /** R7 PLN-01 "Notes". */
    notes: zod_1.z.string().trim().max(exports.INTERVIEW_NOTES_MAX, 'Keep notes under 500 characters.').nullable().optional(),
});
/**
 * R7 F24.08: the planner's date cell and the calendar's drag. Books an interview with no time,
 * moves one that has a time ("We moved it"); a clash is a warning the recruiter may accept.
 */
exports.setInterviewTimeSchema = schemas_1.versionedSchema.extend({
    startsAt: isoSchema,
    isConflictAccepted: zod_1.z.boolean().default(false),
});
/** C5: a panel of up to five, in the planner as in the round. */
exports.PLANNER_PANEL_MAX = 5;
/** R7 F24.08: the planner's Interviewer(s) cell — add, remove or swap, this interview only. */
exports.setInterviewPanelSchema = schemas_1.versionedSchema.extend({
    userIds: zod_1.z.array(schemas_1.uuidSchema).min(1, 'Keep at least one interviewer.').max(exports.PLANNER_PANEL_MAX, `At most ${exports.PLANNER_PANEL_MAX} interviewers.`),
    isConflictAccepted: zod_1.z.boolean().default(false),
});
exports.ROOM_NAME_MAX = 80;
exports.roomInputSchema = zod_1.z.object({
    ventureId: schemas_1.uuidSchema,
    name: zod_1.z.string().trim().min(1, 'This field is required.').max(exports.ROOM_NAME_MAX),
});
exports.updateRoomSchema = schemas_1.versionedSchema.extend({ name: exports.roomInputSchema.shape.name });
exports.INTERVIEW_LIST_VIEWS = ['calendar', 'to_schedule', 'mine'];
exports.interviewListQuerySchema = zod_1.z.object({
    view: zod_1.z.enum(exports.INTERVIEW_LIST_VIEWS).default('calendar'),
    /** Calendar and "mine": the week shown. */
    from: isoSchema.optional(),
    to: isoSchema.optional(),
    jobId: schemas_1.uuidSchema.optional(),
    /** "Mine": past interviews instead of what's coming. */
    isPast: zod_1.z.coerce.boolean().default(false),
});
/** The candidate's booking page sends its link in this header, as the test page does. */
exports.INTERVIEW_LINK_HEADER = 'x-interview-link';
exports.candidateBookSchema = zod_1.z.object({ startsAt: isoSchema });
// --- Words -------------------------------------------------------------------------------
/** Where the candidate goes: the room, the link, or the phone. */
function interviewPlace(mode, roomName, meetingLink) {
    if (mode === 'online')
        return meetingLink ? `Online — ${meetingLink}` : 'Online — the link will follow';
    if (mode === 'phone')
        return 'By phone — we will call you';
    return roomName ?? 'In person — the room will follow';
}
/**
 * What "Copy message" puts on the clipboard for WhatsApp (S19, as S18 Q4), matching the
 * email the candidate was sent.
 */
function interviewMessageText(input) {
    const greeting = `Dear ${input.candidateName},`;
    const sign = `${input.companyName} recruitment team`;
    const about = `your interview for ${input.jobTitle} at ${input.companyName}`;
    switch (input.kind) {
        case 'offer':
            return [greeting, '', `Please choose a time for ${about}:`, input.link, '', sign].join('\n');
        case 'booked':
            return [greeting, '', `This confirms ${about}.`, `When: ${input.when}`, `Where: ${input.place}`, '', `Please confirm you will attend: ${input.link}`, '', sign].join('\n');
        case 'moved':
            return [greeting, '', `The time of ${about} needs to change. We will send you a new time shortly.`, '', sign].join('\n');
        case 'cancelled':
            return [greeting, '', `We are sorry — ${about}, on ${input.when}, is cancelled. We will be in touch.`, '', sign].join('\n');
    }
}
//# sourceMappingURL=interviews.js.map